import { baseApi } from "./baseApi";

const CONSOLIDATION_BASE_URL = "/consolidations";

export const consolidationApi = baseApi.injectEndpoints({
  tagTypes: ["Consolidation"],

  endpoints: (builder) => ({
    addConsolidation: builder.mutation({
      query: (payload) => ({
        url: CONSOLIDATION_BASE_URL,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, { _id }) => [
        { type: "Consolidation", id: _id },
        { type: "Consolidation", id: "LIST" },
      ],
    }),

    listConsolidation: builder.query({
      query: (params) => ({
        url: CONSOLIDATION_BASE_URL,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Consolidation",
              id: _id,
            })),
            { type: "Consolidation", id: "LIST" },
          ]
          : [{ type: "Consolidation", id: "LIST" }],
    }),

    getDDLConsolidation: builder.query({
      query: (params) => ({
        url: `${CONSOLIDATION_BASE_URL}/ddl`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Consolidation",
              id: _id,
            })),
            { type: "Consolidation", id: "DDL" },
          ]
          : [{ type: "Consolidation", id: "DDL" }],
    }),
    getDDLConsolidationForDeconsolidation: builder.query({
      query: (params) => ({
        url: `${CONSOLIDATION_BASE_URL}/ddl/deconsolidation`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Consolidation",
              id: _id,
            })),
            { type: "Consolidation", id: "DDL" },
          ]
          : [{ type: "Consolidation", id: "DDL" }],
    }),

    getConsolidationDetail: builder.query({
      query: (id) => `${CONSOLIDATION_BASE_URL}/${id}`,
      providesTags: (_result, _error, { _id }) => [
        { type: "Consolidation", id: _id },
        { type: "Consolidation", id: "LIST" },
      ],
    }),

    updateConsolidation: builder.mutation({
      query: ({ _id, ...body }) => ({
        url: `${CONSOLIDATION_BASE_URL}/${_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { _id: id }) => [
        { type: "Consolidation", id },
        { type: "Consolidation", id: "LIST" },
      ],
    }),
    // Add Consolidation Note
    addConsolidationNotes: builder.mutation({
      query: (payload) => {
        const { consolidationId, ...body } = payload;

        return {
          url: `${CONSOLIDATION_BASE_URL}/${consolidationId}/notes`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (_result, _error, { consolidationId }) => [
        { type: "Consolidation", id: consolidationId },
        { type: "Consolidation", id: "LIST" },
      ],
    }),

    // Update Consolidation Note
    updateConsolidationNotes: builder.mutation({
      query: ({ consolidationId, noteId, ...body }) => ({
        url: `${CONSOLIDATION_BASE_URL}/${consolidationId}/notes/${noteId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { consolidationId }) => [
        { type: "Consolidation", id: consolidationId },
        { type: "Consolidation", id: "LIST" },
      ],
    }),

    // List Consolidation Notes
    listConsolidationNotes: builder.query({
      query: ({ consolidationId, page, limit }) => ({
        url: `${CONSOLIDATION_BASE_URL}/${consolidationId}/notes`,
        params: { page, limit },
      }),
      providesTags: (_result, _error, { consolidationId }) => [
        { type: "Consolidation", id: consolidationId },
        { type: "Consolidation", id: "LIST" },
      ],
    }),

    listShipmentsByConsolidationId: builder.query({
      query: ({ consolidationId, page, limit }) => ({
        url: `${CONSOLIDATION_BASE_URL}/${consolidationId}/shipments`,
        params: { page, limit },
      }),
      providesTags: (_result, _error, { _id }) => [
        { type: "Consolidation", id: _id },
        { type: "Consolidation", id: "LIST" },
      ],
    }),
    // New endpoint for collected shipments
    getDDLCollectedShipments: builder.query({
      query: (params) => ({
        url: `${CONSOLIDATION_BASE_URL}/ddl/collected`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Consolidation",
              id: _id,
            })),
            { type: "Consolidation", id: "DDL" },
          ]
          : [{ type: "Consolidation", id: "DDL" }],
    }),
    getDDLWarehousingShipments: builder.query({
      query: (params) => ({
        url: `${CONSOLIDATION_BASE_URL}/ddl/warehousing`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Consolidation",
              id: _id,
            })),
            { type: "Consolidation", id: "DDL" },
          ]
          : [{ type: "Consolidation", id: "DDL" }],
    }),

    searchConsolidations: builder.query({
      query: ({ account, entity, query, status }) => ({
        url: `${CONSOLIDATION_BASE_URL}/search`,
        params: { account, entity, query, status },
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result?.data?.map(({ _id }) => ({ type: "Consolidation", _id })),
            { type: "Consolidation", id: "SEARCH" },
          ]
          : [{ type: "Consolidation", id: "SEARCH" }],
    }),
  }),
});

export const {
  useAddConsolidationMutation,
  useListConsolidationQuery,
  useLazyListConsolidationQuery,
  useLazyGetDDLConsolidationQuery,
  useLazyGetDDLConsolidationForDeconsolidationQuery,
  useGetConsolidationDetailQuery,
  useUpdateConsolidationMutation,

  useAddConsolidationNotesMutation,
  useUpdateConsolidationNotesMutation,
  useListConsolidationNotesQuery,
  useLazyListConsolidationNotesQuery,
  seListShipmentsByConsolidationIdQuery,
  useLazyListShipmentsByConsolidationIdQuery,

  useGetDDLCollectedShipmentsQuery,
  useLazyGetDDLCollectedShipmentsQuery,
  useLazyGetDDLWarehousingShipmentsQuery,
  useListShipmentsByConsolidationIdQuery,

  useLazySearchConsolidationsQuery,

} = consolidationApi;
